import React, { useMemo, useState } from 'react';
import { BsAt, BsChatSquareDots, BsLink, BsTrash } from 'react-icons/bs';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Button, Divider, Flex, HStack, Icon, IconButton, Input, Text } from '@chakra-ui/react';
import { useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { Parser } from 'html-to-react';
import PropTypes from 'prop-types';

import TipTapRichTextEditor from '../../../../../../../../../components/TipTapRichTextEditor';
import { getCustomImagePlugin } from '../../../../../../../../../components/TipTapRichTextEditor/CustomImageExtension';
import { patchTipTapHTML } from '../../../../../../../../../components/TipTapRichTextEditor/util';
import { analyticsIds } from '../../../../../../../../../constants/analyticsIds';
import * as UserLevels from '../../../../../../../../../constants/userLevels';
import {
  deleteComment,
  openSubComment,
  selectSubComment, updateComment
} from '../../../../../../../../../redux/modules/activityLog';
import { hideModal } from '../../../../../../../../../redux/modules/modal';
import { selectUser } from '../../../../../../../../../redux/modules/user';
import { ContainerDimensions } from '../../../../../../../../../styles/layoutDimensions';
import { formatActivityTime } from '../../../../../../../../../utils/activityTimeUtil';
import { showDontAskAgainModal } from '../../../../../../../../../utils/askAgainModalUtil';
import ActivityLogAddComment from '../ActivityLogAddComment';
import ActivityLogComments from '../ActivityLogComments';
import ActivityLogUserIcon from '../ActivityLogUserIcon';
import { getActionTypeHeader, parseTaggedUsersToComment } from '../util';

import ActivityLogRowTextErrorBoundary from './ActivityLogRowTextErrorBoundary';

const ActivityLogRow = ({ activity, companyId, insuredId, recordId, jobId }) => {
  const dispatch = useDispatch();
  const addingSubComment = useSelector(selectSubComment);
  const htmlParser = new Parser();
  const currentUser = useSelector(selectUser);
  const {
    id,
    action,
    codeLetter,
    comments,
    createdAt,
    message,
    linked,
    user,
    activityType,
    integration,
    insuranceRequirement,
    job,
    tags
  } = activity;

  const parsedActivityDescription = useMemo(() => {
    try {
      const substrings = ['.com>', '.net>', '.org>', '.gov>', 'â€¢', 'ïƒ ', 'â€¢', 'ïƒ¼', 'Â®', 'ðŸ˜Ž', '•Š'];

      const containsFailureSubstring = substrings.some(substring => message?.includes(substring));

      if (containsFailureSubstring) {
        return `Failed to parse html: \n ${message}`;
      }

      return htmlParser.parse(message);
    } catch (e) {
      console.error('Failed to parse HTML:', { error: e, message });
      return 'FAILED to Parse Message';
    }
  }, [message]);

  const [isEditingComment, setIsEditingComment] = useState(false);

  const editor = useEditor({
    content: message,
    /**
     * This option gives us the control to enable the default behavior of rendering the editor immediately.
     */
    immediatelyRender: true,
    /**
     * This option gives us the control to disable the default behavior of re-rendering the editor on every transaction.
     */
    shouldRerenderOnTransaction: false,
    extensions: [
      StarterKit.configure({
        bulletList: { keepMarks: true },
        orderedList: { keepMarks: true }
      }),
      getCustomImagePlugin(dispatch)
    ],
    editorProps: {
      attributes: {
        class: 'tiptap-editor',
        style: 'min-height: 100px; padding: 8px; border: 1px solid #ccc; border-radius: 4px;'
      }
      // if we want to enable the enter button to post the comment, uncomment below
      // handleKeyDown: (view, event) => {
      //   if (event.key === 'Enter'
      //     && !event.shiftKey
      //     && !event.ctrlKey
      //     && !event.altKey
      //     && !event.metaKey) {
      //     event.preventDefault(); // prevent adding a line break
      //     handleCommentSubmit();
      //     return true;
      //   }
      //
      //   return false;
      // }
    }
  });

  function handleOpeningSubComment() {
    dispatch(openSubComment(id));
  }

  const userCanEdit = useMemo(() => {
    if (activityType !== 'comment') {
      return false;
    }

    return (currentUser.userId === user.id || currentUser.userLevel === 1);
  }, [currentUser, user, activityType]);

  const parsedTags = useMemo(() => {
    if (!tags || !tags?.length) {
      return null;
    }

    const mappedTags = tags.map(tag => ({
      id: tag?.id ?? tag?.userInfo?.userId,
      description: tag?.description ?? `${tag?.userInfo?.userFirstName} ${tag?.userInfo?.userLastName}`,
      userEmail: tag?.userEmail ?? tag?.userInfo?.userEmail
    }));

    return parseTaggedUsersToComment(mappedTags);
  }, [tags]);

  const displayName = useMemo(() => {
    const { firstName, lastName, email, level } = user || {};
    if (firstName || lastName) return `${firstName || ''} ${lastName || ''}`;
    if (integration?.name) return `${integration?.name} integration`;
    if (email && level === UserLevels.Agent) return `Agent ${email}`;
    if (email) return `User ${email}`;

    // null values imply PINS took the action
    return 'PINS System';
  }, [user]);

  const actionAppliedToDetails = useMemo(() => {
    if (!insuranceRequirement?.jobName && (!insuranceRequirement?.id && !job?.name)) {
      return null;
    }

    // when insured level activity/comment, but details are present b/c it pertains to a record
    if (!insuranceRequirement?.jobName) {
      if (insuranceRequirement?.contractNumber) {
        return `${job.name} - ${insuranceRequirement.contractNumber}`;
      }

      if (!job?.name) {
        return null;
      }

      return job.name;
    }

    // otherwise, it's a record level activity/comment
    const { jobName, contractNumber } = insuranceRequirement;

    if (contractNumber) {
      return `${jobName} - ${contractNumber}`;
    }

    return `${jobName}`;
  }, [insuranceRequirement]);

  return (
    <>
      <Flex
        justifyContent="flex-start"
        maxW={ContainerDimensions.maxWidth}
        ml={6}
        mr={10}
        my={4}
      >
        <Box mr={8}>
          <ActivityLogUserIcon
            firstName={user?.firstName || integration?.name || user?.email || 'PINS'}
            fontSize="15px"
            iconSize="40px"
            lastName={user?.lastName || 'System'}
          />
        </Box>
        <Box textAlign="left" w="100%">
          <Flex justifyContent="space-between">
            <Box>
              <Text textStyle="bodyLg500">{displayName}</Text>
            </Box>
            <Box>
              <Text color="greyScale.400" fontSize="sm" textAlign="right">
                {!!createdAt && formatActivityTime(createdAt) }
              </Text>
              {actionAppliedToDetails
                ? (
                  <Text color="greyScale.400" fontSize="sm" textAlign="right">{actionAppliedToDetails}</Text>
                ) : (
                  <Box visibility="hidden">Spacer</Box>
                )}
            </Box>
          </Flex>
          <Text mb={1} textStyle="bodyMd">
            {!!action && getActionTypeHeader(activity, message)}
          </Text>

          {isEditingComment ? (
            <Flex
              justifyContent="flex-start"
              maxW={ContainerDimensions.maxWidth}
              ml={6}
              mr={10}
              my={4}
            >
              <Box
                flexBasis="100%"
                textAlign="left"
              >
                <TipTapRichTextEditor editor={editor} />
              </Box>
            </Flex>
          ) : (
            <ActivityLogRowTextErrorBoundary fallback={<Text textStyle="bodyMd">{`Could not render html: ${message}`}</Text>}>
              <Text textStyle="bodyMd">{parsedActivityDescription}</Text>
            </ActivityLogRowTextErrorBoundary>
          )}

          <Box my={2}>
            { linked
               && (
               <HStack>
                 <Icon as={BsLink} boxSize={6} color="greyScale.400" />
                 <Text color="primaryScale.600" fontSize="xs">{linked}</Text>
               </HStack>
               )}

            { parsedTags
              && (
                <HStack>
                  <Icon as={BsAt} boxSize={6} color="greyScale.400" />
                  <Text color="primaryScale.600" fontSize="xs">{parsedTags}</Text>
                </HStack>
              )}
          </Box>

          <HStack justifyContent="space-between">
            <Box>
              {!isEditingComment && (
                <HStack>
                  <Button
                    data-analytics-id={analyticsIds.insuredProfilePage.activityLog.createSubComment}
                    display={addingSubComment === id ? 'none' : 'flex'}
                    id={id}
                    leftIcon={<Icon as={BsChatSquareDots} boxSize={3} />}
                    px={0}
                    size="sm"
                    variant="textSuccess"
                    onClick={() => handleOpeningSubComment()}
                  >
                    Comment
                  </Button>
                  {userCanEdit && (
                    <>
                      <Button
                        display={addingSubComment === id ? 'none' : 'flex'}
                        variant="textPrimary"
                        onClick={() => setIsEditingComment(!isEditingComment)}
                      >
                        Edit
                      </Button>

                      <IconButton
                        aria-label="Delete"
                        display={addingSubComment === id ? 'none' : 'flex'}
                        icon={<Icon as={BsTrash} />}
                        size="sm"
                        variant="textGrey"
                        onClick={() => showDontAskAgainModal({
                          dispatch,
                          cookieId: 'delete-comment-confirm',
                          description: 'You cannot undo this action.',
                          onClick: () => {
                            dispatch(deleteComment(id));
                            dispatch(hideModal());
                          },
                          title: 'Delete Comment?'
                        })}
                      />
                    </>
                  )}
                </HStack>
              )}
            </Box>

            {isEditingComment && (
              <HStack>
                <Button
                  onClick={() => {
                    dispatch(updateComment(id, patchTipTapHTML(editor.getHTML())));
                    setIsEditingComment(!isEditingComment);
                  }}
                >
                  Save
                </Button>
                <Button
                  display={addingSubComment === id ? 'none' : 'flex'}
                  variant="textPrimary"
                  onClick={() => {
                    editor.commands.setContent(message, false);
                    setIsEditingComment(!isEditingComment);
                  }}
                >
                  Cancel
                </Button>
              </HStack>
            )}
          </HStack>
        </Box>
      </Flex>
      { (addingSubComment === id)
         && (
         <Box ml={16}>
           <ActivityLogAddComment
             subComment
             activityId={id}
             activityType={activityType}
             companyId={companyId}
             insuredId={insuredId}
             jobId={jobId}
             recordId={recordId}
           />
         </Box>
         )}
      <ActivityLogComments activityId={id} comments={comments} />
      <Divider />
    </>
  );
};

// TODO: update propTypes once search endpoint is updated with comments
ActivityLogRow.propTypes = {
  activity: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    action: PropTypes.string,
    user: PropTypes.shape({
      firstName: PropTypes.string,
      lastName: PropTypes.string,
      email: PropTypes.string,
      level: PropTypes.number
    }),
    integration: PropTypes.shape({ name: PropTypes.string }),
    tags: PropTypes.arrayOf(PropTypes.shape({}))
  }),
  companyId: PropTypes.number.isRequired,
  insuredId: PropTypes.number.isRequired,
  recordId: PropTypes.number.isRequired,
  jobId: PropTypes.number.isRequired
};

export default ActivityLogRow;
